import { useId } from 'react';
import { Link } from 'react-router-dom';
import type { ChronumPayrollCategory, ChronumPosition } from '@shared/api';
import { IconChevronDown } from '@shared/ui/icons';
import { FleetChip } from '@entities/fleet';
import { categoryTitle, formatInt } from '@entities/chronum';
import { groupHref } from '../HoursTables';
import { CellMeta } from '../ui';
import type { PositionSave } from '../usePositionSaver';
import {
  CategorySelect,
  DetailsToggle,
  NarrowLabel,
} from './styles';
import { SaveStatus } from './SaveStatus';
import { uniqueCompanies } from './PositionDetails';

export interface RowProps {
  position: ChronumPosition;
  categories: readonly ChronumPayrollCategory[];
  canWrite: boolean;
  save: PositionSave | undefined;
  open: boolean;
  detailsId: string;
  onChoose: (position: ChronumPosition, category: string | null) => void;
  onRetry: (key: string) => void;
  onReread: (key: string) => void;
  onToggle: (key: string) => void;
}

export function PositionRow({
  position: p,
  categories,
  canWrite,
  save,
  open,
  detailsId,
  onChoose,
  onRetry,
  onReread,
  onToggle,
}: RowProps) {
  const selectId = useId();
  const companies = uniqueCompanies(p);
  const workers = p.active_workers === null ? null : formatInt(p.active_workers);
  return (
    <tr className="row" data-open={open ? 'true' : undefined}>
      <th scope="row" className="c-name">
        <span className="name">{p.name}</span>
        {p.active_workers === null && (
          <CellMeta>
            <FleetChip
              $tone="muted"
              title="Должность есть в сопоставлении, но её нет в последнем списке Хронума"
            >
              нет в последнем списке Хронума
            </FleetChip>
          </CellMeta>
        )}
        <CellMeta className="narrow-only">
          {[companies.join(', '), workers !== null ? `работников: ${workers}` : null]
            .filter(Boolean)
            .join(' · ')}
        </CellMeta>
        {p.comment && <CellMeta className="comment">{`Комментарий: ${p.comment}`}</CellMeta>}
        <DetailsToggle
          type="button"
          aria-expanded={open}
          aria-controls={detailsId}
          aria-label={`Комментарий и журнал правок: ${p.name}`}
          onClick={() => onToggle(p.key)}
        >
          {canWrite ? 'Комментарий и журнал' : 'Журнал правок'}
          <IconChevronDown size={14} />
        </DetailsToggle>
      </th>
      <td className={`wide-only c-companies${companies.length ? '' : ' empty'}`}>
        {companies.length ? companies.join(', ') : '—'}
      </td>
      <td className={`wide-only num${workers === null ? ' empty' : ''}`}>{workers ?? '—'}</td>
      <td className="c-category">
        {canWrite ? (
          <>
            <NarrowLabel className="narrow-only" as="label" htmlFor={selectId}>
              Категория ФОТ
            </NarrowLabel>
            <CategorySelect
              id={selectId}
              $unset={!p.payroll_category}
              value={p.payroll_category ?? ''}
              aria-label={`Категория ФОТ: ${p.name}`}
              onChange={(e) => onChoose(p, e.target.value || null)}
            >
              <option value="">Не выбрана</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.title}
                </option>
              ))}
              {p.payroll_category && !categories.some((c) => c.code === p.payroll_category) && (
                <option value={p.payroll_category}>Другая категория</option>
              )}
            </CategorySelect>
            <SaveStatus
              state={save}
              onRetry={() => onRetry(p.key)}
              onReread={() => onReread(p.key)}
            />
          </>
        ) : p.payroll_category ? (
          categoryTitle(p.payroll_category, categories)
        ) : (
          <FleetChip $tone="warning">без категории</FleetChip>
        )}
      </td>
      <td className={`c-group${p.group ? '' : ' empty'}`}>
        <NarrowLabel className="narrow-only">Группа механизаторов</NarrowLabel>
        {p.group ? <Link to={groupHref(p.group.id)}>{p.group.name}</Link> : '—'}
      </td>
    </tr>
  );
}
